import { CircularProgress } from '@mui/material';
import * as React from 'react';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import ReactHotToast from 'react-hot-toast';
import styled from 'styled-components';
import tw from 'twin.macro';

import { Tooltip } from '@/components/Atoms/Tooltip/Tooltip';
import { IconButton } from '@/components/IconButton/IconButton';
import { useOpenFullScreen } from '@/components/SourceCards/hooks/useOpenFullScreen';
import { useAskNeedlDispatchContext } from '@/features/ChatBotV2/context';
import { ASK_NEEDL_ACTIONS } from '@/features/ChatBotV2/hooks/useAskNeedlState';
import type { AttachmentType } from '@/features/ChatBotV2/types';
import { useFetchFeed } from '@/features/CustomFeedV2/hooks';
import { useInitUploadComplete } from '@/hooks/query/useInitUploadComplete';
import CloseIcon from '@/icons/close-sm.svg?react';
import FailedFileIcon from '@/icons/failed-file.svg?react';
import { getFileTypeFromMimeType } from '@/utils/fileAndMimeTypes';
import { getMimeIcon } from '@/utils/getMimeIcons';

const StyledIconButton = styled(IconButton)`
  ${tw`absolute p-[0.5px] -top-2 -right-2 bg-gray-300 z-10`}
`;

const StyledCloseIcon = styled(CloseIcon)`
  ${tw`text-gray-700 fill-current`}
`;

export const AttachmentCard = ({
  id,
  name,
  mime_type,
  bucket,
  fileKey,
  status,
  feed_id,
  progress,
  showRemoveButton = false,
}: AttachmentCardType) => {
  const dispatch = useAskNeedlDispatchContext();
  const { removeFile } = useInitUploadComplete();

  const { data: feedData, isLoading } = useFetchFeed(feed_id as string);
  const doc = React.useMemo(() => {
    return feedData?.documents?.find((doc) => doc.id === id);
  }, [feedData, id]);
  const { openFullScreen } = useOpenFullScreen({
    result: {
      access_key: doc?.access_key,
      deeplink: doc?.deeplink,
      board_id: 'home',
      channel_id: null,
      category_tab: 'drives',
      source: 'needldrive',
    },
  });

  const removeHandler = async () => {
    dispatch({
      type: ASK_NEEDL_ACTIONS.REMOVE_ATTACHMENT,
      payload: { attachmentId: id },
    });
    if (['COMPLETED', 'PROCESSING_ERROR'].includes(status)) {
      await removeFile(
        {
          source: 'ask_needl',
          feed_id: feed_id as string,
          files: [
            {
              id,
              name,
              mime_type: getFileTypeFromMimeType(mime_type) as string,
              bucket: bucket as string,
              key: fileKey as string,
            },
          ],
        },
        {
          onRemoveError: (error, payload) => {
            ReactHotToast.error(
              `Error removing file: ${payload.files[0].name} ${
                error.message || error
              }`
            );
          },
        }
      );
    }
  };

  return (
    <button
      className={`relative flex items-center gap-2 border-gray-300 border rounded-md py-1 px-2 w-40 ${
        isLoading ? 'cursor-not-allowed' : 'hover:bg-gray-100'
      } active:bg-gray-200`}
      onClick={() => {
        if (doc?.access_key) {
          openFullScreen();
        }
      }}
      disabled={isLoading}
    >
      {showRemoveButton ? (
        <StyledIconButton
          type='button'
          size='xs'
          circular={false}
          background
          onClick={(e) => {
            e.stopPropagation();
            removeHandler();
          }}
        >
          <StyledCloseIcon />
        </StyledIconButton>
      ) : null}
      <div className='w-5 h-5 flex items-center justify-center'>
        <MimeIconDisplay
          mimeType={mime_type}
          status={status}
          progress={progress}
        />
      </div>
      <div className='flex flex-col'>
        <Tooltip label={name}>
          <span className='sub-heading-xs line-clamp-1 break-all'>{name}</span>
        </Tooltip>
        <span className='body-xxs'>{mime_type.toUpperCase()}</span>
      </div>
    </button>
  );
};

const MimeIconDisplay = ({
  mimeType,
  progress,
  status,
}: MimeIconDisplayType) => {
  const MimeIcon = getMimeIcon(mimeType);

  if (status === 'UPLOADING') {
    return (
      <CircularProgressbar
        className='w-5 h-5'
        styles={buildStyles({
          pathColor: '#367d8d',
          textColor: '#545856',
          textSize: '28px',
        })}
        value={progress as number}
      />
    );
  }

  if (['PROCESSING', 'PENDING', 'UPLOADED'].includes(status)) {
    return <CircularProgress size='18px' />;
  }

  if (['PROCESSING_ERROR', 'UPLOADING_ERROR'].includes(status)) {
    return <FailedFileIcon className='w-5 h-5' />;
  }

  return <MimeIcon className='w-5 h-5' />;
};

type AttachmentCardType = Pick<
  AttachmentType,
  'bucket' | 'id' | 'mime_type' | 'name' | 'progress' | 'status'
> & {
  showRemoveButton?: boolean;
  fileKey?: string;
  feed_id?: string;
};

type MimeIconDisplayType = {
  mimeType: string;
  status: AttachmentType['status'];
  progress?: number;
};
